import React from 'react';
import { RubyLoader } from '../../components/RubyLoader';
import type { BuildHealthRow } from './buildHealthModel';

const VERDICT: Record<BuildHealthRow['verdict'], { text: string; cls: string }> = {
  healthy: { text: 'Healthy', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  watch: { text: 'Watch', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  worse: { text: 'Worse than current', cls: 'bg-red-50 text-red-700 border-red-200' },
  'no-data': { text: 'Not enough data', cls: 'bg-stone-50 text-stone-500 border-stone-200' },
};

export default function BuildHealthCard({ rows, loading }: { rows: BuildHealthRow[]; loading: boolean }) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="mb-3 text-[14.5px] font-bold tracking-tight text-stone-900">Health by build · last 7 days</div>
      {loading && <div data-testid="build-health-loading"><RubyLoader variant="section" className="py-8" /></div>}
      {!loading && rows.length === 0 && <div className="text-[13px] text-stone-500">No live builds reporting yet.</div>}
      {!loading && rows.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {rows.map((r) => (
            <div key={r.version} className="rounded-xl border border-stone-200 p-4">
              <div className="font-mono text-[13px] font-bold text-stone-900">{r.version}</div>
              <div className="text-[11px] text-stone-500">{r.devices} device{r.devices === 1 ? '' : 's'} · {r.sessions} sessions</div>
              <div className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-stone-900">{r.crashFreePct === null ? '—' : `${r.crashFreePct.toFixed(1)}%`}</div>
              <div className="text-[11px] text-stone-500">crash-free · {r.errorsPerSession === null ? '—' : r.errorsPerSession} errors/session</div>
              <div className={`text-[11px] ${r.payAnomalies !== null && r.payAnomalies > 0 ? 'font-semibold text-rose-600' : 'text-stone-500'}`}>
                {r.payAnomalies === null ? '—' : r.payAnomalies} payment anomal{r.payAnomalies === 1 ? 'y' : 'ies'}
              </div>
              <span className={`mt-2 inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${VERDICT[r.verdict].cls}`}>{VERDICT[r.verdict].text}</span>
              {r.summary && <div className="mt-2 text-[11px] leading-snug text-stone-600">{r.summary}</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
